import clsx from 'clsx';

import {
  LoggedInAgencyUserFieldsFragment,
  PreviewUpdateSubscriptionResultFieldsFragment
} from '~/api/graphql';

const formatUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);

const SkeletonRow = ({ labelWidth }: { labelWidth: string }) => (
  <div className="flex justify-between items-center">
    <div className={clsx('h-3 rounded bg-neutral-200 dark:bg-neutral-700', labelWidth)} />
    <div className="h-3 w-14 rounded bg-neutral-200 dark:bg-neutral-700" />
  </div>
);

/**
 * Stand-in for InvoicePreview while the first preview is being fetched. It
 * mirrors the real layout so the totals do not shift into place once they
 * arrive.
 */
export const InvoicePreviewSkeleton = ({ compact }: { compact?: boolean }) => (
  <div
    role="status"
    aria-label="Calculating your total"
    className={clsx(
      'animate-pulse bg-neutral-50 dark:bg-neutral-700 rounded-md',
      compact ? 'px-4 py-2' : 'p-6'
    )}
  >
    <div className="space-y-2 border-b border-neutral-200 dark:border-neutral-700 py-2">
      <SkeletonRow labelWidth="w-44" />
    </div>
    <div className="space-y-2 border-b border-neutral-200 dark:border-neutral-700 py-2">
      <SkeletonRow labelWidth="w-20" />
      <SkeletonRow labelWidth="w-24" />
    </div>
    <div className="space-y-2 py-2">
      <SkeletonRow labelWidth="w-12" />
    </div>
  </div>
);

export const InvoicePreview = ({
  invoicePreview,
  agencyUser,
  compact
}: {
  invoicePreview: PreviewUpdateSubscriptionResultFieldsFragment['invoicePreview'];
  agencyUser: LoggedInAgencyUserFieldsFragment;
  /** Tighter padding and no explanatory footnotes, for space-constrained dialogs. */
  compact?: boolean;
}) => {
  if (!invoicePreview) return null;

  return (
    <div
      className={
        compact
          ? 'bg-neutral-50 dark:bg-neutral-700 rounded-md px-4 py-2'
          : 'bg-neutral-50 dark:bg-neutral-700 rounded-md p-6'
      }
    >
      {/* Line Items */}
      {invoicePreview.lineItems.length > 0 && (
        <div className="space-y-2 border-b border-neutral-200 dark:border-neutral-700 py-2">
          {invoicePreview.lineItems.map((item, index) => (
            <div key={index} className="flex justify-between items-center">
              <div className="flex-1">
                <div className="text-sm text-neutral-900 dark:text-white">{item.name}</div>
              </div>
              <div className="text-sm text-neutral-900 dark:text-white">
                {formatUSD(item.totalAmount)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Subtotal */}
      {(invoicePreview.subTotal > 0 ||
        invoicePreview.discountTotal > 0 ||
        invoicePreview.taxTotal > 0) && (
        <div className="space-y-2 border-b border-neutral-200 dark:border-neutral-700 py-2">
          {invoicePreview.subTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Subtotal</span>
              <span className="text-neutral-900 dark:text-white">
                {formatUSD(invoicePreview.subTotal)}
              </span>
            </div>
          )}

          {/* Discount */}
          {invoicePreview.discountTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Discount</span>
              <span className="text-green-600 dark:text-green-400">
                -{formatUSD(invoicePreview.discountTotal)}
              </span>
            </div>
          )}

          {/* Tax */}
          {invoicePreview.taxTotal > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600 dark:text-neutral-400">Tax</span>
              <span className="text-neutral-900 dark:text-white">
                {formatUSD(invoicePreview.taxTotal)}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2 py-2">
        {/* Total */}
        <div className="flex justify-between text-sm font-medium">
          <span className="text-neutral-900 dark:text-white">Total</span>
          <span className="text-neutral-900 dark:text-white">
            {formatUSD(invoicePreview.total)}
          </span>
        </div>
      </div>

      {compact ? null : (
        <div className="py-2">
          {!invoicePreview.isNextInvoice && (
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              This is a preview of the recurring invoice that will be generated for your
              subscription. If you've already made a payment and are making a change in the middle
              of a billing cycle, you will receive a prorated credit for the remaining time in the
              current billing cycle, which will be automatically applied to future invoices until
              the credit is depleted.
            </p>
          )}
          {invoicePreview.isNextInvoice && (
            <p className="text-xs text-neutral-600 dark:text-neutral-400">
              This is a preview of the next invoice that will be generated for your subscription. If
              you've made any changes, you'll pay this amount upon confirmation of the subscription
              update. Otherwise, you'll pay this amount at the start of the next billing cycle. Any
              negative balance will be automatically applied as a credit to future invoices until
              the credit is depleted.
            </p>
          )}
          {agencyUser?.agency.subscription?.trialEndDate &&
            new Date(agencyUser.agency.subscription.trialEndDate) > new Date() && (
              <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
                You won't be charged until your trial ends on{' '}
                {new Date(agencyUser.agency.subscription.trialEndDate).toLocaleDateString()}.
              </p>
            )}
        </div>
      )}
    </div>
  );
};
